import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROLES } from './AppShell';
import { eligibilityApi, shortDate } from '../lib/eligibilityApi';
import type { ExamEligibility } from '../lib/eligibilityApi';

/**
 * THE EXAM-ELIGIBILITY LINE ON THE COMMAND CENTRE.
 *
 * ⚠ IT RENDERS NOTHING WHEN THERE IS NOTHING TO SAY. A dashboard that always shows an alert box
 * teaches the reader to stop seeing it, and by the second week the one that matters is invisible
 * too. This appears only when somebody is actually below the line, and disappears again when the
 * school is clear — which is what makes it worth looking at.
 *
 * ⚠ It also fails silent on error rather than showing a red box, for the same reason PresenceStrip
 * does: it hangs off the first screen a trustee opens, and a supporting endpoint 403ing for a role
 * mapped wrong must not be the first thing they see.
 */
export function EligibilityAlert({ institutionId }: { institutionId?: string | null }) {
  const { user } = useAuth();
  const [d, setD] = useState<ExamEligibility | null>(null);

  const canSeeContact = !!user && (ROLES.hr as readonly string[]).includes(user.role);

  useEffect(() => {
    let alive = true;
    const q = { institutionId: institutionId ?? null };
    eligibilityApi
      .watchlist({ ...q, withContact: canSeeContact })
      .catch(() => (canSeeContact ? eligibilityApi.watchlist(q) : Promise.reject(new Error('no'))))
      .then((r) => alive && setD(r))
      .catch(() => alive && setD(null));
    return () => {
      alive = false;
    };
  }, [institutionId, canSeeContact]);

  if (!d) return null;

  const atRisk = d.condonation + d.detained;
  if (atRisk === 0 && d.cieShortfallStudents === 0) return null;

  // The classes carrying the most stuck students — that is where a phone call goes first.
  const worst = [...d.classes]
    .filter((c) => c.unrecoverable > 0 || c.detained > 0)
    .sort((a, b) => (b.unrecoverable - a.unrecoverable) || (b.detained - a.detained))
    .slice(0, 3);

  const urgent = d.unrecoverable > 0;

  return (
    <div className="card elig-alert" style={{
      padding: 16, marginBottom: 16,
      borderLeft: `3px solid var(${urgent ? '--danger' : '--warn'})`,
    }}>
      <div className="elig-alert-head">
        <div>
          <div className="viz-title" style={{ marginBottom: 2 }}>
            Exam eligibility — {shortDate(d.cutOff)}
          </div>
          <p className="viz-sub" style={{ marginBottom: 0 }}>
            {urgent ? (
              <>
                <strong style={{ color: 'var(--danger)' }}>{d.unrecoverable} student
                {d.unrecoverable === 1 ? '' : 's'} can no longer reach {d.eligibleThreshold}%</strong>{' '}
                before {shortDate(d.cutOff)}, whatever happens from tomorrow. {atRisk} are short in total,
                with {d.teachingDaysLeft} teaching days left.
              </>
            ) : (
              <>
                {atRisk} student{atRisk === 1 ? '' : 's'} below {d.eligibleThreshold}%, and{' '}
                {d.teachingDaysLeft} teaching days left to fix it.
              </>
            )}
          </p>
        </div>
        <Link to="/exam-eligibility" className="btn btn-ghost" style={{ whiteSpace: 'nowrap' }}>
          See who →
        </Link>
      </div>

      {worst.length > 0 && (
        <ul className="elig-alert-list">
          {worst.map((c) => (
            <li key={c.classKey}>
              <strong>{c.classLabel}</strong>
              <span className="elig-alert-meta">
                {c.unrecoverable > 0 && ` · ${c.unrecoverable} out of time`}
                {c.detained > 0 && ` · ${c.detained} below the floor`}
                {c.classTeacherName && ` · ${c.classTeacherName}`}
              </span>
              {/* One tap, from the phone in the principal's hand. */}
              {c.classTeacherMobile && (
                <a href={`tel:${c.classTeacherMobile}`} className="pill pill-ok"
                   style={{ fontSize: 10.5, marginLeft: 8, textDecoration: 'none' }}>
                  ☎ {c.classTeacherMobile}
                </a>
              )}
              {c.classTeacherOnLeaveToday && (
                <span className="pill pill-warn" style={{ fontSize: 10, marginLeft: 6 }}>on leave today</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {d.cieShortfallStudents > 0 && (
        <p className="elig-alert-meta" style={{ marginTop: 8, marginBottom: 0 }}>
          Separately, {d.cieShortfallStudents} student(s) are below {d.minCiePercent}% of the CIE in at
          least one course and cannot sit its SEE on that ground alone.
        </p>
      )}
    </div>
  );
}
